import OrderedMap from "orderedmap";
import { DOMSerializer, Fragment, Schema, Slice, } from "prosemirror-model";
import { Plugin } from "prosemirror-state";
import { suggest } from "prosemirror-suggest";
export class Extension {
    addNodes(nodes) {
        return nodes;
    }
    addMarks(marks) {
        return marks;
    }
    addPlugins(schema, plugins, _context) {
        return plugins;
    }
    addNodeViews(schema, nodeViews, portalContainer) {
        return nodeViews;
    }
    addSlashCommands(commands) {
        return commands;
    }
    initializeView(view) {
        return view;
    }
    addSuggesters(suggesters, _slashCommands) {
        return suggesters;
    }
    addCommands(_schema) {
        return {};
    }
}
export function generateNodes(extensions) {
    return extensions.reduce((acc, extension) => extension.addNodes(acc), OrderedMap.from({}));
}
export function generateMarks(extensions) {
    return extensions.reduce((acc, extension) => extension.addMarks(acc), OrderedMap.from({}));
}
export function generateNodeViews(schema, extensions, portalContainer) {
    return extensions.reduce((acc, extension) => extension.addNodeViews(schema, acc, portalContainer), {});
}
export function generateSchema(extensions) {
    const nodes = generateNodes(extensions);
    const marks = generateMarks(extensions);
    // for some reason, we're getting an error: "Schema is missing its top node type ('doc')"
    // when giving `nodes` directly to Schema constructor, so we need to convert back to an object
    // TODO: consider not using OrderedMap in the Extension `addNodes` method.
    const nodesDict = {};
    nodes.forEach((key, value) => (nodesDict[key] = value));
    const marksDict = {};
    marks.forEach((key, value) => (marksDict[key] = value));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Schema({ nodes: nodesDict, marks: marksDict });
}
export function generateSuggestersPlugin(extensions) {
    const slashCommands = generateSlashCommandSpecs(extensions);
    const specs = extensions.reduce((acc, extension) => extension.addSuggesters(acc, slashCommands), []);
    return suggest(...specs);
}
export function generateSlashCommandSpecs(extensions) {
    return extensions.reduce((acc, extension) => extension.addSlashCommands(acc), []);
}
export function generatePlugins(schema, extensions, portalContainer) {
    const commands = generateCommands(schema, extensions);
    return [
        generateSuggestersPlugin(extensions),
        clipboardPlugin(),
        ...extensions.reduce((acc, extension) => extension.addPlugins(schema, acc, { commands, portalContainer }), []),
    ];
}
export function generateCommands(schema, extensions) {
    return extensions.reduce((commands, extension) => ({
        ...commands,
        ...extension.addCommands(schema),
    }), {});
}
export function initializeView(view, extensions) {
    extensions.forEach((extension) => extension.initializeView(view));
}
export function clipboardPlugin() {
    return new Plugin({
        props: {
            handleDOMEvents: {
                copy(view, event) {
                    var _a, _b, _c;
                    try {
                        const { state } = view;
                        const { selection } = state;
                        if (selection.empty) {
                            // Nothing to copy — let native behavior handle it
                            return false;
                        }
                        const content = selection.content();
                        const rawValue = content.content.toJSON();
                        (_a = event.clipboardData) === null || _a === void 0 ? void 0 : _a.setData("application/x-smartdoc", JSON.stringify(rawValue));
                        // Optional plain text fallback
                        (_b = event.clipboardData) === null || _b === void 0 ? void 0 : _b.setData("text/plain", view.state.doc.textBetween(selection.from, selection.to, "\n"));
                        // word, slack etc
                        const div = document.createElement("div");
                        const fragment = DOMSerializer.fromSchema(view.state.schema).serializeFragment(content.content);
                        div.appendChild(fragment);
                        (_c = event.clipboardData) === null || _c === void 0 ? void 0 : _c.setData("text/html", div.innerHTML);
                        event.preventDefault();
                        return true;
                    }
                    catch (err) {
                        // Don't preventDefault — allow browser/ProseMirror to handle copy
                        return false;
                    }
                },
                paste(view, event) {
                    var _a;
                    const customData = (_a = event.clipboardData) === null || _a === void 0 ? void 0 : _a.getData("application/x-smartdoc");
                    if (customData) {
                        try {
                            const json = JSON.parse(customData);
                            const { schema } = view.state;
                            let content;
                            if (Array.isArray(json)) {
                                content = Fragment.fromJSON(schema, json);
                            }
                            else {
                                const node = schema.nodeFromJSON(json);
                                content = Fragment.from(node);
                            }
                            const tr = view.state.tr.replaceSelection(new Slice(content, 0, 0));
                            view.dispatch(tr.scrollIntoView());
                            event.preventDefault();
                            return true;
                        }
                        catch (_b) {
                            // No preventDefault, allow native paste fallback
                        }
                    }
                    return false;
                },
            },
        },
    });
}
